import { Entity, EntityLink } from '../entity';
import { Attachment } from './attachment';
import { ActivityShareType, ActivitySource, Shareable } from './activity.sharing';
import { Location } from './location';
import { NotifiableContactEntity } from '../contact/contact.options';
import { WithConnectStamp } from '../connect/connect.stamp';
import { ActivityEntityName } from './activity.entity.name';
import { ActivityKind, ActivityType } from './activity.type';
import type { JobConstructionEvidence } from '../planned.work/visit/job.construction.evidence';


export type CrmEntityName = ActivityEntityName.Customer | ActivityEntityName.Supplier | ActivityEntityName.Contractor | ActivityEntityName.ContactEmployee | ActivityEntityName.LoginContact | ActivityEntityName.BasicContact;

export enum ActivityDisplayMode {
    ReadOnly = 'ReadOnly',
    Edit = 'Edit',
    New = 'New'
}

export enum ActivityGroupName {
    Checklist = 'Checklist'
}


export enum ActivityTagName {
    CustomerId = 'CustomerId',
    WorkOrderId = 'WorkOrderId',
    JobId = 'JobId',
    CodeId = 'CodeId',
    ChecklistId = 'ChecklistId',
    QuestionId = 'QuestionId',
    Type = 'ActivityType',
    Group = 'ActivityGroup',
    Resource = 'ResourceGroup',
    PurchaseOrderId = 'PurchaseOrderId',
    InvoiceId = 'InvoiceId',
    PrepaymentInvoiceId = 'PrepaymentInvoiceId',
    PrepaymentSupplierId = 'PrepaymentSupplierId',
    BillId = 'BillId',
    TechnicianId = 'TechnicianId',
    ComplianceId = 'ComplianceId',
    ContactId = 'ContactId',
    AllocationId = 'AllocationId',
    UtilityId = 'UtilityId',
    RecurringJobId = 'RecurrentJobId',
    ResourceId = 'ResourceId',
    Opportunity = 'Opportunity',
    ContractorId = 'ContractorId',
    Lead = 'Lead',
    QuotePro = 'QuotePro',
    Project = 'Project',
    Payment = 'Payment',
    Quote = 'Quote',
    Site = 'Site',
    ClaimContractorId = 'ClaimContractorId',
    ContractorActivityId = 'ContractorActivityId',
    CustomerActivityId = 'CustomerActivityId',
    AssetId = 'AssetId',
    WorkId = 'WorkId',
    ServicePlanId = 'ServicePlanId',
    MemoStreamSerial = 'MemoStreamSerial',
    MemoSerial = 'MemoSerial',
    // HomesVIC extension-of-time day count. Written by the BGIS EOT form handler and intentionally
    // unread today — retained for the eventual AURQ path, WBS-11048 (see bgis.form.handler.ts).
    ExtnReqDays = 'ExtnReqDays',
    // HomesNSW's credit note number. A credit note is not a WorkBuddy record — it exists only as the
    // activity the HomesNSW feed writes, so this tag is the only way a later file or comment naming
    // that credit note can find which job or notice it belongs to.
    HomesCreditNoteNumber = 'HomesCreditNoteNumber',
    IntegrationId = 'IntegrationId',
}

export enum ActivitySendStatus {
    None = '',
    Pending = 'Queued',
    Sent = 'Sent',
    SentByContactor = 'Sent By Contactor',
    Failed = 'Failed',
    //Handles situation for legacy emails where we do not check the status
    //but we dont want it to be processed again anyway
    LegacySent = 'LegacySent',
    Scheduled = 'Scheduled'
}

export enum ActivityPhoneDirectionType {
    Inbound = 'Inbound',
    Outbound = 'Outbound'
}

export enum ActivityPhoneOutComeType {
    Connected = 'Connected',
    LeftMessage = 'Left Message',
    LeftVoicemail = 'Left Voicemail',
    Busy = 'Busy',
    NoAnswer = 'No Answer',
    WrongNumber = 'Wrong Number',
    InvalidNumber = 'Invalid Number'
}

export interface ActivityTag {
    name: ActivityTagName;
    value: any;
    data?: Entity;
}

export interface ActivitySyncRecipient {
    by?: string;
    date?: Date;
    error?: string;
}

export interface ActivityExport {
    _id: string;
    type: ActivityType;
    name: string;
    note: string;
    attachments?: Attachment[];
    createdDate?: Date;
    updateDate?: Date;
    sent?: ActivitySyncRecipient;
}

export type ActivitySendSchedule = 'Immediate' | 'BusinessHours';

export interface SendableActivity {

    /** This is used for the sender an email or sms activity */
    from?: NotifiableContactEntity;

    /** This is used for the receiver an email or sms activity */
    to?: NotifiableContactEntity[];

    /** This is used for the cc receiver an email */
    cc?: NotifiableContactEntity[];

    /** This is used for the bcc receiver an email */
    bcc?: NotifiableContactEntity[];

    sendStatus?: ActivitySendStatus;
    sendDate?: Date;
    sendError?: string;

    /** This is used to determine how the activity should be sent, immediate or business hours, the purpose of setting the value is to determine how to set the scheduledSendTime */
    sendSchedule?: ActivitySendSchedule;

    /** This is used to determine when the activity should be sent, it is set by the sendSchedule, the sendSchedule is used to determine if the activity should be sent immediately or at a later time */
    scheduledSendTime?: Date;

    /** This is used to determine when the activity was last attempted to be sent, it is set by the sendSchedule, the sendSchedule is used to determine if the activity should be sent immediately or at a later time */
    lastAttemptedAt?: Date;      // UTC

    /** Not used yet */
    lockedUntil?: Date;

    /** Not used yet */
    retryCount?: number;

    /** Not used yet */
    noReschedule?: boolean;
}

export interface Activity extends SendableActivity, Shareable, WithConnectStamp {

    _id: string;
    active: boolean;

    type: ActivityType;
    kind: ActivityKind;

    entity: ActivityEntityName;
    entityId: string;

    /** This is used to an name or the subject of and email activity */
    name?: string;

    /** This is used to as a note or the body of and email activity */
    note: string;

    subTitle?: string;

    attachments?: Attachment[];
    claimedAttachments?: Attachment[];

    createdDate?: Date;
    updateDate?: Date;
    updateBy?: Entity;
    createdBy?: Entity;

    // updatedBy?: Entity;
    system: boolean;
    tags: ActivityTag[];
    tenantName?: string;

    sensitive?: boolean;
    systemSharing?: boolean;
    shared?: ActivityShareType[];

    // createdDateString?: string;
    source?: ActivitySource;

    sent?: ActivitySyncRecipient;
    received?: ActivitySyncRecipient;

    isDraft?: boolean;

    /** This is used to store tags */
    labels?: Entity[];


    /** @deprecated never really used */
    tenantId?: string;

    /** @deprecated use tags */
    parentId?: string;

    /** @deprecated use tags */
    customerId?: string;

    /** @deprecated use tags */
    questionId?: string;


    /** handle track location when field app check in/stop */
    location?: Location;

    links?: EntityLink[];
    timezone?: string;

    effectiveSharing?: Shareable;

    markEntitySent?: boolean;
    includeEntityLink?: boolean;

    callDirection?: ActivityPhoneDirectionType;
    callOutcome?: ActivityPhoneOutComeType;
    callDate?: Date;
    callDuration?: number;

    /** Links to upstream activity for cross-tenant sync parity */
    upstreamActivityId?: string;
    /** Read-only flag for downstream copies of upstream activities */
    readOnly?: boolean;

    /** Groups activities into a conversation thread. The root activity has threadId === its own _id. Child activities reference the root's _id. */
    threadId?: string;
    /** When true, the thread is resolved and no further replies can be added */
    threadResolved?: boolean;

    /** Job-created activity only: why the Job was authorised, per contributing Visit (WBS-11806). */
    construction?: JobConstructionEvidence;
}

interface SmsActivityMembers {

    _id: string;
    active: boolean;
    system: boolean;

    kind: ActivityKind;
    type: ActivityType;

    entity: ActivityEntityName;
    entityId: string;

    from: NotifiableContactEntity;
    to: NotifiableContactEntity[];

    name: string;
    note: string;

    createdDate?: Date;
    updateDate?: Date;
    updateBy?: Entity;
    createdBy?: Entity;

    sendStatus?: ActivitySendStatus;
    sendDate?: Date;
    sendError?: string;

    tags: ActivityTag[];

    sensitive?: boolean;
    shared?: ActivityShareType[];

    /*** This is used to store tags */
    labels?: Entity[];
}

interface EmailActivityMembers {

    _id: string;
    active: boolean;
    system: boolean;

    kind: ActivityKind;
    type: ActivityType;

    entity: ActivityEntityName;
    entityId: string;

    from?: NotifiableContactEntity
    to?: NotifiableContactEntity[];
    cc?: NotifiableContactEntity[];
    bcc?: NotifiableContactEntity[];

    name: string;
    note: string;
    attachments?: Attachment[];

    createdDate?: Date;
    updateDate?: Date;
    updateBy?: Entity;
    createdBy?: Entity;

    sendStatus?: ActivitySendStatus;
    sendDate?: Date;
    sendError?: string;

    tags: ActivityTag[];
    sensitive?: boolean;
    shared?: ActivityShareType[];

    /** This is used to store tags */
    labels?: Entity[];

}

export type SmsActivity = Pick<Activity, keyof SmsActivityMembers>;
export type EmailActivity = Pick<Activity, keyof EmailActivityMembers>;

export type ActivitySearchOptions =
    {
        /** This flag is used to restrict activity searching to the the entityId field only and will ignore tags eg. The customer activities in CRM */
        restrictSearchToEntityId?: boolean;
    }
    & {
        /** This flag is used to restrict activity searching to the the entityType field only and will ignore tags eg. The utility activities needs this flag */
        restrictSearchToEntityType?: boolean;
    }
    & {
        /** This flag is used to restrict activity searching to the the entityType field only and will ignore tags eg. The utility activities needs this flag */
        restrictSearchToEntityTypeAndId?: boolean;
        entityName?: ActivityEntityName;
    }
    & {
        /** This flag is used to turn on the feature to display all stage activities when getting activities in field app */
        showAllFieldActivities?: boolean;
    }
    & {
        /** Filter by activity kinds */
        kinds?: ActivityKind[];
    }
